/**
 * The clinic app's live-update socket.
 *
 * One connection per signed-in tab. The server only ever says "notifications
 * changed" (with the clinic's counts); on that signal the screens that show
 * notifications, counts or the new-patient queue refetch through the normal
 * authenticated API. Nothing clinical arrives over the socket itself.
 *
 * Reconnects with a growing delay, and when the server closes with 4401 (the
 * access token expired while the tab was idle) it asks the API client to
 * refresh the session first, then retries with the new token.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, tokens } from './api';

export interface NotificationCounts {
  open: number;
  unread: number;
  critical: number;
  high: number;
  byModule: Record<string, number>;
}

type Status = 'connecting' | 'live' | 'offline';

/** The query keys a "notifications changed" event makes stale. */
export const LIVE_QUERY_KEYS = [
  ['notifications'],
  ['notification-counts'],
  ['nav-counts'],
  ['treatment-queue'],
  ['physician-alerts'],
  ['dashboard-stats'],
] as const;

export function useRealtime(enabled: boolean): Status {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('connecting');

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let retryTimer: number | null = null;
    let attempts = 0;
    let stopped = false;

    const refreshAll = () => {
      for (const key of LIVE_QUERY_KEYS) void queryClient.invalidateQueries({ queryKey: [...key] });
    };

    const scheduleRetry = () => {
      if (stopped) return;
      attempts += 1;
      // 1s, 2s, 4s ... capped at 30s, so a restarting API is not hammered.
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempts - 1, 5));
      retryTimer = window.setTimeout(connect, delay);
    };

    function connect() {
      const token = tokens.access;
      if (!token || stopped) return;
      setStatus('connecting');
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(`${scheme}://${window.location.host}/api/v1/realtime`);

      socket.onopen = () => socket?.send(JSON.stringify({ type: 'auth', token }));

      socket.onmessage = (event) => {
        let message: { type?: string; counts?: NotificationCounts } = {};
        try {
          message = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (message.type === 'ready') {
          attempts = 0;
          setStatus('live');
          // Anything that changed while we were disconnected.
          refreshAll();
        } else if (message.type === 'notifications.changed') {
          if (message.counts) queryClient.setQueryData(['notification-counts'], message.counts);
          refreshAll();
        }
      };

      socket.onclose = (event) => {
        setStatus('offline');
        if (stopped) return;
        if (event.code === 4401) {
          // Any authenticated call refreshes an expired access token.
          void api
            .get('/auth/me')
            .catch(() => undefined)
            .finally(scheduleRetry);
          return;
        }
        scheduleRetry();
      };
    }

    connect();

    return () => {
      stopped = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [enabled, queryClient]);

  return status;
}
